import React, { useEffect, useState, useMemo } from 'react'
import { List, ListItem, ListItemText } from '@mui/material'
import { makeStyles, withStyles } from '@mui/styles'
import { CustomCheckbox } from '../../components/restyled-mui/CustomInputs'

export const CustomList = withStyles(_theme => ({
  root: {
    padding: 0,
  },
}))(List)

export const CustomListItemText = withStyles(_theme => ({
  root: {},
}))(ListItemText)

const useStyles = makeStyles(theme => ({
  listContainer: {
    overflowY: 'auto',
    border: `1px solid ${theme.palette.table.cellBorder}`,
    marginTop: 26,
    marginBottom: 20,
    minHeight: 0,
    maxHeight: '325px',
  },

  zebraItem: {
    borderBottom: `1px solid ${theme.palette.table.cellBorder}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette.table.cellBg,
    },
  },

  listItemRoot: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  listItemText: {
    '& .MuiListItemText-primary': {
      fontSize: 14,
    },
  },

  selectAllItem: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    fontWeight: 600,
  },
}))

const SetupsSelections = props => {
  const classes = useStyles(props)
  const {
    allSetups,
    user,
    setUser,
    searchSetupName,
    setFormState,
    creating,
    sortMode,
  } = props

  const [syncPermissions, setSyncModulePermissions] = useState([])
  const userSetups = user.setups

  useEffect(() => {
    const syncLists = allSetups.map(setup => ({
      name: setup.name,
      id: setup.id,
      enabled: setup.enabled,
      checked: userSetups.includes(setup.name),
    }))
    setSyncModulePermissions(syncLists)
  }, [allSetups])

  const updateSetupsState = updatedPermissions => {
    setSyncModulePermissions(updatedPermissions)

    const updatedChecked = updatedPermissions
      .filter(permission => permission.checked)
      .map(setup => setup.id)

    // Get selected AND enabled setups
    const selectedEnabledSetups = updatedPermissions
      .filter(permission => permission.checked && permission.enabled)
      .map(setup => setup.name)

    setUser(prev => ({ ...prev, setups: updatedChecked }))

    setFormState(prev => ({
      ...prev,
      setups: updatedChecked,
      setupsError:
        creating && updatedChecked.length === 0
          ? 'User creation requires at least one setup'
          : '',
      userSelectedSetups: selectedEnabledSetups, // Update this in formState
      selectedUserSetupsError:
        !creating && selectedEnabledSetups.length === 0
          ? 'User requires at least one selected setup'
          : '',
    }))
  }

  const toggleAllSetups = () => {
    const allVisibleEnabledChecked =
      filteredSetups.filter(item => item.enabled).length > 0 &&
      filteredSetups.filter(item => item.enabled).every(item => item.checked)

    // Toggle to the opposite state
    const isChecked = !allVisibleEnabledChecked

    const newSyncList = syncPermissions.map(permission => {
      if (
        filteredSetups.some(item => item.id === permission.id) &&
        permission.enabled
      ) {
        return { ...permission, checked: isChecked }
      }
      return permission
    })

    updateSetupsState(newSyncList)
  }

  const handleCheckboxClick = id => {
    const updatedPermissions = syncPermissions.map(permission => {
      if (permission.id === id) {
        return { ...permission, checked: !permission.checked }
      }
      return permission
    })

    updateSetupsState(updatedPermissions)
  }

  const filteredSetups = useMemo(() => {
    const filtered = syncPermissions.filter(item =>
      item.name.toLowerCase().includes(searchSetupName.toLowerCase()),
    )

    if (sortMode === 'alphabetical') {
      return filtered.sort((a, b) => a.name.localeCompare(b.name))
    } else {
      // Sort selected setups first, then alphabetically
      return filtered.sort((a, b) => {
        const aSelected = a.checked
        const bSelected = b.checked
        if (aSelected && !bSelected) return -1
        if (!aSelected && bSelected) return 1
        return a.name.localeCompare(b.name)
      })
    }
  }, [syncPermissions, searchSetupName, sortMode])

  const allVisibleEnabledChecked =
    filteredSetups.filter(p => p.enabled).length > 0 &&
    filteredSetups.filter(p => p.enabled).every(p => p.checked)

  const indeterminate =
    filteredSetups.some(p => p.enabled && p.checked) &&
    filteredSetups.some(p => p.enabled && !p.checked)

  return (
    <div className={classes.listContainer}>
      <CustomList disablePadding dense>
        {/* ✅ Select All row (same structure as your working component) */}
        <ListItem
          className={`${classes.zebraItem} ${classes.listItemRoot} ${classes.selectAllItem}`}
          secondaryAction={
            <CustomCheckbox
              indeterminate={indeterminate}
              checked={allVisibleEnabledChecked}
              onChange={toggleAllSetups}
            />
          }
        >
          <CustomListItemText
            primary="Select All"
            className={classes.listItemText}
          />
        </ListItem>

        {/* ✅ Setup rows */}
        {filteredSetups.map(setup => (
          <ListItem
            key={setup.id}
            className={`${classes.zebraItem} ${classes.listItemRoot}`}
            secondaryAction={
              <CustomCheckbox
                checked={setup.checked}
                disabled={!setup.enabled}
                onChange={() => handleCheckboxClick(setup.id)}
              />
            }
          >
            <CustomListItemText
              primary={setup.name}
              className={classes.listItemText}
              style={{
                textDecoration: !setup.enabled ? 'line-through' : 'none',
                color: !setup.enabled ? '#999' : 'inherit',
                opacity: !setup.enabled ? 0.6 : 1,
              }}
            />
          </ListItem>
        ))}
      </CustomList>
    </div>
  )
}

export default SetupsSelections
